import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    GaugeIcon,
    UsersThreeIcon,
    FileTextIcon,
    BroadcastIcon,
    ChartBarIcon,
    GearSixIcon,
    SignOutIcon,
    PhoneCallIcon,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: GaugeIcon, testid: "nav-dashboard" },
    { to: "/prospects", label: "Prospects", icon: UsersThreeIcon, testid: "nav-prospects" },
    { to: "/scripts", label: "Scripts", icon: FileTextIcon, testid: "nav-scripts" },
    { to: "/campaigns", label: "Campaigns", icon: BroadcastIcon, testid: "nav-campaigns" },
    { to: "/calls", label: "Calls", icon: PhoneCallIcon, testid: "nav-calls" },
    { to: "/results", label: "Results", icon: ChartBarIcon, testid: "nav-results" },
    { to: "/settings", label: "Settings", icon: GearSixIcon, testid: "nav-settings" },
];

export default function AppShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    return (
        <div className="min-h-screen flex bg-white">
            {/* Sidebar */}
            <aside className="w-64 shrink-0 border-r border-[var(--border)] bg-[var(--surface)] flex flex-col" data-testid="app-sidebar">
                <div className="px-6 py-6 border-b border-[var(--border)]">
                    <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-link">
                        <div className="w-7 h-7 bg-[var(--primary)] flex items-center justify-center" aria-hidden="true">
                            <span className="text-white font-mono text-[11px] font-bold">N*</span>
                        </div>
                        <div>
                            <div className="font-bold text-[15px] tracking-tight">NORTHSTAR</div>
                            <div className="overline text-[9px] mt-0.5">Sales Calling OS</div>
                        </div>
                    </Link>
                </div>
                <nav className="flex-1 py-4 px-3" aria-label="Primary">
                    {NAV.map(({ to, label, icon: Icon, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 mb-0.5 text-[14px] transition-colors duration-150 ${
                                    isActive
                                        ? "bg-[var(--primary)] text-white font-semibold"
                                        : "text-[var(--text-primary)] hover:bg-[var(--surface-active)]"
                                }`
                            }
                        >
                            <Icon size={18} weight="bold" aria-hidden="true" />
                            <span>{label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="border-t border-[var(--border)] p-4">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 bg-[var(--primary)] text-white flex items-center justify-center text-[13px] font-semibold" aria-hidden="true">
                            {user?.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="min-w-0">
                            <div className="text-[13px] font-semibold truncate">{user?.name}</div>
                            <div className="text-[11px] text-[var(--text-secondary)] font-mono truncate">{user?.email}</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={logout}
                        data-testid="logout-button"
                        className="btn-ghost w-full px-3 py-2 text-[13px] flex items-center justify-center gap-2"
                    >
                        <SignOutIcon size={14} weight="bold" /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 overflow-y-auto" data-testid="app-main">
                <Outlet context={{ navigate }} />
            </main>
        </div>
    );
}
